import {
    ClientDetailsFormData,
    DirectClientDetailsFormData,
    FileKind,
    GetQuoteDocumentRequest,
    IdentificationType,
    IntermediaryTypes,
    PersonDetails,
    PersonDetailsRwanda,
    PersonKYCDetails,
    QuoteState,
    RecommendedPlan,
    SelectPlanData,
    SelectPlanRequest,
    TravelCoverType
} from 'Application/models';
import { QuoteService, UploadFileService } from 'Application/service';
import {
    convertToEnumKey,
    deleteDirectClientQuotationDataFromStorage,
    getDirectClientQuotationDataFromStorage,
    saveDirectClientQuotationDataToStorage,
    savePendingQuotationStepToSessionStorage
} from 'Application/utils';
import { create } from 'zustand';

const initialMainCustomerObj = {
    firstName: '',
    otherNames: '',
    phoneNumber: '',
    emailAddress: '',
    dateOfBirth: '',
    nationality: '',
    occupation: {
        code: '',
        description: ''
    },
    gender: '',
    taxNumber: '',
    placeOfBirth: '',
    identificationType: '',
    identificationNumber: '',
    passportFile: null,
    kraFile: null,
    medicalFile: null,
    studentFile: null
};

const initialMainCustomerRwanda = {
    salutation: '',
    firstName: '',
    otherNames: '',
    placeOfBirth: '',
    identificationNumber: '',
    identificationType: '',
    birthProvince: null,
    birthDistrict: null,
    birthSector: null,
    birthCell: null,
    birthVillage: null,
    maritalStatus: '',

    nkRecordType: '',
    nkNationality: '',
    nkFirstName: '',
    nkOtherNames: '',
    nkGender: '',
    nkRelationship: '',
    nkPhoneNumber: '',
    nkEmailAddress: '',
    nkDateOfBirth: '',
    nkIdentificationType: '',
    nkIdentificationNumber: '',
    nkAddress: '',

    permanentCountry: '',
    residentCountry: '',
    residentProvince: null,
    residentDistrict: null,
    residentSector: null,
    residentCell: null,
    residentVillage: null,
    residentStreetNumber: '',
    residentHouseNumber: '',
    residenceType: '',
    postalAddress: '',
    postalCode: '',
    postalBox: '',

    recordType: '',
    employerName: '',
    employerId: '',
    incomeRange: '',
    incomeFrequency: '',
    occupationGroup: {
        code: '',
        description: ''
    },
    occupationSubGroup: {
        code: '',
        description: ''
    },
    occupationOption: {
        code: '',
        description: ''
    },
    politicallyExposedStatus: '',
    healthInsuranceNumber: '',
    socialSecurityNumber: '',
    highestEducationLevel: '',

    businessSector: '',
    economicSubSector: {
        code: '',
        description: ''
    },
    policyHolderRelation: '',
    relationShipType: '',
    relationShipPartyName: '',
    socialEconomicClass: '',

    businessSegment: '',
    businessChannel: '',
    vatExempt: '',

    phoneNumber: '',
    emailAddress: '',
    dateOfBirth: '',
    taxNumber: '',
    nationality: '',
    gender: '',
    nicsCode: '',
    occupation: {
        code: '',
        description: ''
    },
    kraFile: null,
    passportFile: null,
    medicalFile: null,
    studentFile: null,
    nationalIdFile: null
};

export const useQuoteStore = create<QuoteState>((set, get) => ({
    isLoading: false,
    isDownloadFileLoading: false,
    isError: false,
    error: null,
    quotationNumber: null,
    quoteId: null,
    clientDetailsData: null,
    directClientDetails: null,
    recommendedPlans: [],
    recommendedPlansBenefits: [],
    selectedPlan: null,
    isCovidSelected: false,
    isWarAndTerrorismSelected: false,
    mainCustomer: initialMainCustomerObj,
    mainCustomerRwanda: initialMainCustomerRwanda,
    dependants: [],
    dependantsRwanda: [],
    quoteDetailsData: null,
    quoteDocument: null,
    rwandaClientPassportNumber: '',
    intermediaryType: null,
    setIntermediaryType(intermediaryType: IntermediaryTypes | null) {
        set({ intermediaryType: intermediaryType });
    },

    resetQuoteStore: () => {
        set({
            isLoading: false,
            isError: false,
            error: null,
            quotationNumber: null,
            quoteId: null,
            clientDetailsData: null,
            recommendedPlans: [],
            recommendedPlansBenefits: [],
            selectedPlan: null,
            isCovidSelected: false,
            isWarAndTerrorismSelected: false,
            mainCustomer: initialMainCustomerObj,
            mainCustomerRwanda: initialMainCustomerRwanda,
            dependants: [],
            dependantsRwanda: [],
            quoteDetailsData: null,
            quoteDocument: null,
            rwandaClientPassportNumber: '',
            intermediaryType: null
        });
    },

    createClientDetails: async (clientDetailsFormData: ClientDetailsFormData) => {
        set({ isLoading: true });

        if (JSON.stringify(clientDetailsFormData) === JSON.stringify(get().clientDetailsData)) {
            set({ isLoading: false });
            return;
        }

        const { customReason, ...clientDetailsRequestData } = clientDetailsFormData;

        if (clientDetailsRequestData.reasonOfTravel === 'Other') {
            clientDetailsRequestData.reasonOfTravel = customReason;
        }

        try {
            const response = await QuoteService.addQuoteClientDetails(clientDetailsRequestData);

            set({
                clientDetailsData: clientDetailsFormData,
                rwandaClientPassportNumber: clientDetailsFormData.clientPassportNumber,
                isError: false,
                error: null,
                quotationNumber: response.quotationNumber,
                quoteId: response.quoteId,
                recommendedPlans: response.plans,
                recommendedPlansBenefits: response.benefits
            });
        } catch (error) {
            set({ error: 'Failed to create quote', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    createDirectClientDetails: async (directClientDetails: DirectClientDetailsFormData) => {
        set({ isLoading: true });

        if (JSON.stringify(directClientDetails) === JSON.stringify(get().directClientDetails)) {
            set({ isLoading: false });
            return;
        }

        const { customReason, ...clientDetailsRequestData } = directClientDetails;

        if (clientDetailsRequestData.reasonOfTravel === 'Other') {
            clientDetailsRequestData.reasonOfTravel = customReason;
        }

        try {
            const response = await QuoteService.addQuoteDirectClientDetails(clientDetailsRequestData);

            set({
                directClientDetails: directClientDetails,
                clientDetailsData: directClientDetails,
                isError: false,
                error: null,
                quotationNumber: response.quotationNumber,
                quoteId: response.quoteId,
                recommendedPlans: response.plans,
                recommendedPlansBenefits: response.benefits
            });
        } catch (error) {
            set({ error: 'Failed to create quote', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    selectQuotePlan: async (selectPlanData: SelectPlanData, isDirectClient?: boolean) => {
        const selectedPlan: RecommendedPlan = {
            ...get().recommendedPlans.find((plan) => plan.travelPlanId === selectPlanData.travelPlanId)!
        };

        const warAndTerrorismCost = (selectedPlan.warPercentage / 100) * selectedPlan.totalPremiums;
        const covidCost = (selectedPlan.covidPercentage / 100) * selectedPlan.totalPremiums;

        let totalPremiumCost = selectedPlan.totalPremiums;

        if (selectPlanData.includeWarAndTerrorism) {
            totalPremiumCost += warAndTerrorismCost;
        }

        if (selectPlanData.includeCovid) {
            totalPremiumCost += covidCost;
        }

        set({
            isLoading: true,
            selectedPlan: selectedPlan
        });

        const typeOfTrip = Object.keys(TravelCoverType).find(
            (key) => (key as keyof typeof TravelCoverType).toString() === selectPlanData.typeOfTrip
        )!;

        const quotePlanDetails: SelectPlanRequest = {
            planId: get().selectedPlan?.travelPlanId!,
            coverType: typeOfTrip,
            totalPremiumCost: totalPremiumCost,
            warTerrorismCheck: selectPlanData.includeWarAndTerrorism,
            covidExtensionCheck: selectPlanData.includeCovid,
            region: selectPlanData.region
        };

        try {
            if (isDirectClient) {
                await QuoteService.addQuotePlanDetailsDirectClient(get().quotationNumber!, quotePlanDetails);
            } else {
                await QuoteService.addQuotePlanDetails(get().quotationNumber!, quotePlanDetails);
            }

            set({
                isError: false,
                error: null
            });
        } catch (error) {
            set({ error: 'Failed to add quote plan details', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    createKYCDetails: async (mainCustomerData: PersonDetails, dependantsData: PersonDetails[]) => {
        set({ isLoading: true });

        try {
            const { studentFile, kraFile, passportFile, medicalFile } = mainCustomerData;

            const mainCustomerFiles = [studentFile, kraFile, passportFile, medicalFile].filter((file) => file !== null);

            const mainCustomerKYCData: PersonKYCDetails = {
                firstName: mainCustomerData.firstName,
                otherNames: mainCustomerData.otherNames,
                emailAddress: mainCustomerData.emailAddress,
                phoneNumber: mainCustomerData.phoneNumber,
                dateOfBirth: mainCustomerData.dateOfBirth,
                identificationNumber: mainCustomerData.identificationNumber,
                identificationType: IdentificationType.PASSPORT.toUpperCase(),
                nationality: mainCustomerData.nationality,
                gender: mainCustomerData.gender,
                taxNumber: mainCustomerData.taxNumber,
                occupation: mainCustomerData.occupation.code,
                filenames: mainCustomerFiles.length > 0 ? mainCustomerFiles.map((mainCustomerFile) => mainCustomerFile.fileName) : undefined
            };

            const dependantsKYCData: PersonKYCDetails[] = dependantsData.map((dependant) => {
                const {
                    studentFile: dependantStudentFile,
                    kraFile: dependantKraFile,
                    passportFile: dependantPassportFile,
                    medicalFile: dependantMedicalFile
                } = dependant;

                const dependantFiles = [dependantStudentFile, dependantKraFile, dependantPassportFile, dependantMedicalFile].filter(
                    (file) => file !== null
                );

                return {
                    firstName: dependant.firstName,
                    otherNames: dependant.otherNames,
                    emailAddress: dependant.emailAddress,
                    phoneNumber: dependant.phoneNumber,
                    dateOfBirth: dependant.dateOfBirth,
                    identificationNumber: dependant.identificationNumber,
                    identificationType: IdentificationType.PASSPORT.toUpperCase(),
                    filenames: dependantFiles.length > 0 ? dependantFiles.map((dependantFile) => dependantFile.fileName) : undefined
                };
            });

            await QuoteService.addQuoteKYCDetails(get().quotationNumber!, {
                mainCustomer: mainCustomerKYCData,
                dependants: dependantsKYCData,
                hasConsented: true
            });
        } catch (error) {
            set({ error: 'Failed to add KYC details for quote customers', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    createKYCDetailsRwanda: async (mainCustomerData: PersonDetailsRwanda, dependantsData: PersonDetailsRwanda[]) => {
        set({ isLoading: true });

        try {
            const { studentFile, kraFile, passportFile, medicalFile, nationalIdFile } = mainCustomerData;

            const mainCustomerFiles = [studentFile, kraFile, passportFile, medicalFile, nationalIdFile].filter((file) => file !== null);

            const mainCustomerKYCData: PersonKYCDetails = {
                salutation: convertToEnumKey(mainCustomerData.salutation),
                firstName: mainCustomerData.firstName,
                otherNames: mainCustomerData.otherNames,
                emailAddress: mainCustomerData.emailAddress,
                phoneNumber: mainCustomerData.phoneNumber,
                dateOfBirth: mainCustomerData.dateOfBirth,
                identificationNumber: mainCustomerData.identificationNumber,
                identificationType: convertToEnumKey(mainCustomerData.identificationType),
                taxNumber: mainCustomerData.taxNumber,
                nationality: mainCustomerData.nationality,
                gender: mainCustomerData.gender,
                occupation: mainCustomerData.occupation.code,
                filenames: mainCustomerFiles.map((mainCustomerFile) => mainCustomerFile.fileName),
                maritalStatus: convertToEnumKey(mainCustomerData.maritalStatus),
                businessChannel: mainCustomerData.businessChannel,
                placeOfBirth: {
                    country: mainCustomerData.placeOfBirth,
                    province: mainCustomerData.birthProvince?.provinceCode,
                    district: mainCustomerData.birthDistrict?.districtCode,
                    sector: mainCustomerData.birthSector?.code,
                    cell: mainCustomerData.birthCell?.code,
                    village: mainCustomerData.birthVillage?.code
                },
                permanentCountry: mainCustomerData.permanentCountry,
                residenceAddress: {
                    country: mainCustomerData.residentCountry,
                    province: mainCustomerData.residentProvince?.provinceCode,
                    district: mainCustomerData.residentDistrict?.districtCode,
                    sector: mainCustomerData.residentSector?.code,
                    cell: mainCustomerData.residentCell?.code,
                    village: mainCustomerData.residentVillage?.code,
                    residenceType: mainCustomerData.residenceType,
                    streetNumber: mainCustomerData.residentStreetNumber,
                    houseNumber: mainCustomerData.residentHouseNumber
                },
                postalBox: mainCustomerData.postalBox,
                postalCode: mainCustomerData.postalCode,
                physicalAddress: mainCustomerData.postalAddress,
                economicSector: mainCustomerData.economicSubSector.code,
                relationshipToBritam: mainCustomerData.relationshipToBritam,
                employmentDetails: {
                    employerName: mainCustomerData.employerName,
                    employeeId: mainCustomerData.employerId,
                    incomeRange: mainCustomerData.incomeRange,
                    incomeFrequency: mainCustomerData.incomeFrequency,
                    occupationGroup: mainCustomerData.occupationGroup.code,
                    occupationSubGroup: mainCustomerData.occupationSubGroup.code,
                    occupationOption: mainCustomerData.occupationOption.code
                },
                ...(mainCustomerData.maritalStatus.toUpperCase() === 'MARRIED' && {
                    nextOfKin: {
                        recordType: mainCustomerData.nkRecordType,
                        firstName: mainCustomerData.nkFirstName,
                        otherNames: mainCustomerData.nkOtherNames,
                        dateOfBirth: mainCustomerData.nkDateOfBirth,
                        gender: mainCustomerData.nkGender,
                        relationship: mainCustomerData.nkRelationship,
                        phoneNumber: mainCustomerData.nkPhoneNumber,
                        emailAddress: mainCustomerData.nkEmailAddress,
                        identificationType: convertToEnumKey(mainCustomerData.nkIdentificationType),
                        identificationNumber: mainCustomerData.nkIdentificationNumber,
                        address: mainCustomerData.nkAddress,
                        nationality: mainCustomerData.nkNationality
                    }
                }),
                socialEconomicClass: mainCustomerData.socialEconomicClass,
                businessSegment: mainCustomerData.businessSegment,
                relationShipPartyName: mainCustomerData.relationShipPartyName,
                politicallyExposedStatus: mainCustomerData.politicallyExposedStatus,
                nicscode: mainCustomerData.nicsCode
            };

            const dependantsKYCData = dependantsData.map((dependant) => {
                const { passportFile: dependantPassportFile } = dependant;

                const dependantFiles = [dependantPassportFile].filter((file) => file !== null);

                return {
                    passportNumber: dependant.identificationNumber,
                    fullName: dependant.fullName,
                    dateOfBirth: dependant.dateOfBirth,
                    fileName: dependantFiles[0].fileName
                };
            });

            await QuoteService.addQuoteKYCDetails(get().quotationNumber!, {
                mainCustomer: mainCustomerKYCData,
                dependants: [],
                // otherTravellerDetails: dependantsKYCData,
                hasConsented: true
            });
        } catch (error) {
            set({ error: 'Failed to add KYC details for quote customers', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    saveMainCustomerKYCDetails: (updatedDetails: Partial<PersonDetails>) => {
        set((state) => {
            return {
                mainCustomer: {
                    ...state.mainCustomer,
                    ...updatedDetails
                }
            };
        });
    },

    saveMainCustomerRwandaKYCDetails: (updatedDetails: Partial<PersonDetailsRwanda>) => {
        set((state) => {
            return {
                mainCustomerRwanda: {
                    ...state.mainCustomerRwanda,
                    ...updatedDetails
                }
            };
        });
    },

    saveDependantsKYCDetails: (index: number, updatedDetails: Partial<PersonDetails>) => {
        set((state) => {
            const updatedDependants = [...state.dependants];
            updatedDependants[index] = { ...updatedDependants[index], ...updatedDetails };
            return { dependants: updatedDependants };
        });
    },

    saveDependantsRwandaKYCDetails: (index: number, updatedDetails: Partial<PersonDetailsRwanda>) => {
        set((state) => {
            const updatedDependants = [...state.dependantsRwanda];
            updatedDependants[index] = { ...updatedDependants[index], ...updatedDetails };
            return { dependantsRwanda: updatedDependants };
        });
    },

    uploadKYCFile: async (passportNumber: string, attachment: File, fileKind: FileKind) => {
        set({ isLoading: true, isError: false, error: null });

        try {
            const response = await UploadFileService.uploadFile(passportNumber, attachment, fileKind);
            return response.documentName;
        } catch (error) {
            set({ error: `Failed to upload KYC attachment for passport ${passportNumber}`, isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getQuoteDetails: async (quoteId?: string) => {
        set({ isLoading: true });

        const quoteIdToUse = quoteId || get().quoteId;

        try {
            if (!quoteIdToUse) {
                throw new Error('Quotation number is required');
            }

            const response = await QuoteService.getQuoteDetails(quoteIdToUse);

            set({
                isError: false,
                error: null,
                quoteDetailsData: response,
                isLoading: false,
                quotationNumber: response.quotation.quotationNumber,
                rwandaClientPassportNumber: response.quotation.clientPassportNumber,
                clientDetailsData: {
                    sourceLocation: response.quotation.sourceLocation,
                    destination: response.quotation.travelDestinations.map((destination) => destination.destinationName),
                    departureDate: response.quotation.departureDate,

                    //* Balance between the two
                    customReason: response.quotation.reasonOfTravel,
                    reasonOfTravel: response.quotation.reasonOfTravel,

                    returnDate: response.quotation.returnDate,
                    dateOfBirth: response.quotation.clientDateOfBirth,
                    travelType: response.quotation.travelType,
                    //* Number of other travellers
                    numOfOtherTravellers: response.quotation.countOfDependants,

                    travellersInLowerAgeRange: response.quotation.travellersInLowerAgeRange,
                    travellersInMedianAgeRange: response.quotation.travellersInMedianAgeRange,
                    travellersInUpperAgeRange: response.quotation.travellersInUpperAgeRange,
                    agencyName: response.quotation.intermediary ? response.quotation.intermediary.name : null
                },

                selectedPlan:
                    response.plan === null
                        ? null
                        : {
                              travelPlanName: response.plan.name,
                              travelPlanId: response.plan.planID,
                              covidPercentage: response.plan.covidPercentage,
                              warPercentage: response.plan.warPercentage,
                              totalPremiums: response.quotation.totalPremium,
                              currencyType: response.plan.currencyType
                          },
                isCovidSelected: response.quotation.coverCovidExtension,
                isWarAndTerrorismSelected: response.quotation.coverWarAndTerrorism
            });
        } catch (error) {
            set({ error: 'Failed to get quote details', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getRecommendedPlans: async (quotationNumber?: string) => {
        set({ isLoading: true });

        const quoteNumberToUse = quotationNumber || get().quotationNumber;

        try {
            if (!quoteNumberToUse) {
                throw new Error('Quotation number is required');
            }

            const response = await QuoteService.getRecommendedPlans(quoteNumberToUse);

            set({
                isError: false,
                error: null,
                recommendedPlans: response.plans,
                recommendedPlansBenefits: response.benefits
            });

            let indexToSet = 0;

            //* Know which step to take the user depending on the details present
            if (get().selectedPlan === null) {
                indexToSet = 1;
            } else if (get().selectedPlan !== null && get().mainCustomer.identificationNumber === '') {
                indexToSet = 2;
            } else if (get().mainCustomer.identificationNumber === '') {
                indexToSet = 3;
            } else if (get().mainCustomer.identificationNumber !== '') {
                indexToSet = 4;
            }

            savePendingQuotationStepToSessionStorage(indexToSet);
        } catch (error) {
            set({ error: 'Failed to get recommended plans by quotation number', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getQuoteDocument: async (quotationNumber: string, getQuoteDocumentDetails: GetQuoteDocumentRequest) => {
        set({ isLoading: true });

        try {
            const response = await QuoteService.getQuoteDocument(quotationNumber, getQuoteDocumentDetails);

            set({
                isError: false,
                error: null
            });

            return response;
        } catch (error) {
            set({ error: 'Failed to get quote document', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    //* Used by direct client alone
    saveDirectClientQuotationDataToLocalStorage: () => {
        saveDirectClientQuotationDataToStorage(
            get().quotationNumber!,
            get().directClientDetails!,
            get().recommendedPlans,
            get().recommendedPlansBenefits,
            get().selectedPlan!,
            get().isCovidSelected,
            get().isWarAndTerrorismSelected
        );
    },

    refreshStoreFromLocalStorageDirectClient: () => {
        const data = getDirectClientQuotationDataFromStorage();

        if (data) {
            const {
                quotationNumber,
                directClientDetails,
                recommendedPlans,
                recommendedPlansBenefits,
                selectedPlan,
                isCovidSelected,
                isWarAndTerrorismSelected
            } = data;

            set({
                quotationNumber,
                directClientDetails,
                recommendedPlans,
                recommendedPlansBenefits,
                selectedPlan,
                isCovidSelected,
                isWarAndTerrorismSelected
            });

            deleteDirectClientQuotationDataFromStorage();
        }
    }
}));
